/**
 * Create payloads for Organisation Structure nodes.
 *
 * Shared by the tree's Add dialog ({@link NodeConfigPanel}) and Platform
 * Config's organisation setup (CR-8227), so both create groups, divisions and
 * sites through the same endpoints with the same fields — there is one way to
 * create a company, not two.
 */
import type { Company, CompanyGroup, CompanyProfileDetail } from '../../lib/api/types';
import type { NodeConfigForm } from './useNodeConfigForm';

/**
 * Empty string → null so a cleared number field stores as "not specified".
 *
 * @param value - the input's text
 * @returns the parsed integer, or null when blank or not a number
 */
export const toNumberOrNull = (value: string): number | null => {
  if (!value.trim()) return null;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * CR-6247: the nested profile payload the company endpoint accepts.
 *
 * @param form - the node form values
 * @returns employee count, year established and annual revenue
 */
export function buildProfilePayload(form: NodeConfigForm): CompanyProfileDetail {
  return {
    employee_count: toNumberOrNull(form.employeeCount),
    year_established: toNumberOrNull(form.yearEstablished),
    annual_revenue: form.annualRevenue.trim() || null,
  };
}

/**
 * A group or division create. A division IS a CompanyGroup: `level` is derived
 * from `parent_group` on save, so the parent is what makes it a division.
 *
 * @param form - the node form values
 * @param options.parentGroup - the parent group's id, for a division
 * @param options.companyType - the tab it was created on, so an empty group stays there
 * @returns the body for `coreAPI.createCompanyGroup`
 */
export function buildGroupCreatePayload(
  form: NodeConfigForm,
  options: { parentGroup?: number; companyType?: 'customer' | 'supplier' },
): Partial<CompanyGroup> {
  return {
    name: form.name,
    description: form.description,
    headquarters_country: form.country || undefined,
    website: form.website || undefined,
    parent_group: options.parentGroup || undefined,
    company_type: options.companyType,
    is_active: form.isActive,
  };
}

/**
 * A site (company) create. CR-6691: created active, with the full address.
 *
 * @param form - the node form values
 * @param options.companyType - customer or supplier
 * @param options.group - the group or division the site sits in, if any
 * @param options.baseCurrency - the currency to store, when one should be sent
 * @returns the body for `coreAPI.createCompany`
 */
export function buildCompanyCreatePayload(
  form: NodeConfigForm,
  options: { companyType: 'customer' | 'supplier'; group?: number; baseCurrency?: string },
): Partial<Company> {
  return {
    name: form.name,
    address_line_1: form.addressLine1 || undefined,
    address_line_2: form.addressLine2 || undefined,
    city: form.city || undefined,
    state: form.stateCounty || undefined,
    postal_code: form.postalCode || undefined,
    country: form.country || undefined,
    website: form.website || undefined,
    primary_email: form.primaryEmail || undefined,
    company_type: options.companyType,
    // CR-6247: expanded field set saves in the same request.
    registration_number: form.registrationNumber || undefined,
    base_currency: options.baseCurrency,
    profile: buildProfilePayload(form),
    group: options.group,
    is_active: true,
  } as Partial<Company>;
}
